import type { Filtri, Periodo } from './controlli.ts';
import { opzioniFiltri } from './controlli.ts';
import type { Dipendente } from '../api/tipi.ts';
import stili from './BarraCalendario.module.css';

/**
 * Controlli del calendario: periodo, scorrimento, filtri e ricerca.
 *
 * La disposizione ricalca quella di un calendario da ufficio — frecce a
 * sinistra, periodo al centro, scelta della vista a destra — perche' e' un
 * modello che il personale ha gia' in mano e non deve imparare.
 */

export interface ProprietaBarra {
  periodo: Periodo;
  etichettaPeriodo: string;
  filtri: Filtri;
  dipendenti: readonly Dipendente[];
  risultatiRicerca: number;
  onPeriodo: (periodo: Periodo) => void;
  onScorri: (passi: number) => void;
  onOggi: () => void;
  onFiltri: (filtri: Filtri) => void;
}

const PERIODI: { codice: Periodo; etichetta: string }[] = [
  { codice: 'settimana', etichetta: 'Settimana' },
  { codice: 'mese', etichetta: 'Mese' },
  { codice: 'anno', etichetta: 'Anno' },
];

export function BarraCalendario({
  periodo,
  etichettaPeriodo,
  filtri,
  dipendenti,
  risultatiRicerca,
  onPeriodo,
  onScorri,
  onOggi,
  onFiltri,
}: ProprietaBarra) {
  const { sedi, reparti } = opzioniFiltri(dipendenti, filtri.sedeCodice);
  const scorrimentoAttivo = periodo !== 'anno';

  return (
    <div className={stili.barra}>
      <div className={stili.riga}>
        <button type="button" className={stili.pulsante} onClick={onOggi}>
          Oggi
        </button>

        <div className={stili.frecce}>
          <button
            type="button"
            className={stili.freccia}
            onClick={() => onScorri(-1)}
            disabled={!scorrimentoAttivo}
            aria-label="Periodo precedente"
          >
            ‹
          </button>
          <button
            type="button"
            className={stili.freccia}
            onClick={() => onScorri(1)}
            disabled={!scorrimentoAttivo}
            aria-label="Periodo successivo"
          >
            ›
          </button>
        </div>

        <span className={stili.periodo}>{etichettaPeriodo}</span>

        <div className={stili.viste} role="group" aria-label="Vista">
          {PERIODI.map((p) => (
            <button
              key={p.codice}
              type="button"
              className={`${stili.vista} ${periodo === p.codice ? stili.vistaAttiva : ''}`}
              onClick={() => onPeriodo(p.codice)}
              aria-pressed={periodo === p.codice}
            >
              {p.etichetta}
            </button>
          ))}
        </div>
      </div>

      <div className={stili.riga}>
        <select
          className={stili.select}
          value={filtri.sedeCodice ?? ''}
          onChange={(e) =>
            // Cambiando sede il reparto scelto potrebbe non esistere piu':
            // azzerarlo evita una griglia vuota senza spiegazione.
            onFiltri({
              ...filtri,
              sedeCodice: e.target.value || null,
              repartoCodice: null,
            })
          }
          aria-label="Filtra per sede"
        >
          <option value="">Tutte le sedi</option>
          {sedi.map((s) => (
            <option key={s.codice} value={s.codice}>
              {s.nome}
            </option>
          ))}
        </select>

        <select
          className={stili.select}
          value={filtri.repartoCodice ?? ''}
          onChange={(e) => onFiltri({ ...filtri, repartoCodice: e.target.value || null })}
          aria-label="Filtra per reparto"
        >
          <option value="">Tutti i reparti</option>
          {reparti.map((r) => (
            <option key={r.codice} value={r.codice}>
              {r.nome}
            </option>
          ))}
        </select>

        <div className={stili.ricerca}>
          <input
            type="search"
            className={stili.input}
            placeholder="Cerca una persona…"
            value={filtri.ricerca}
            onChange={(e) => onFiltri({ ...filtri, ricerca: e.target.value })}
            aria-label="Cerca una persona"
          />
          {filtri.ricerca !== '' && (
            <span className={stili.esitoRicerca}>
              {risultatiRicerca === 0
                ? 'nessuno'
                : risultatiRicerca === 1
                  ? '1 trovato'
                  : `${risultatiRicerca} trovati`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
